// Motor de resolución de colisiones para el drag & resize de widgets.
//
// El algoritmo viejo, cuando dos cards se superponían, buscaba el próximo
// hueco libre escaneando de a columnas/filas (+220px, wrap de fila) - eso
// podía mandar una card lejísimos de donde estaba y "desbaratar" el resto
// del dashboard con un solo movimiento.
//
// Este en cambio empuja cada card vecina lo MÍNIMO indispensable para
// dejar de superponerse con la card que se está moviendo/redimensionando
// (separación por el eje -x o y- donde el solapamiento es menor), y solo
// si ese empujón hace que choque con una tercera, la sigue empujando en
// cascada. La card que se está arrastrando nunca se mueve a sí misma acá:
// actúa como obstáculo fijo, todo lo demás se acomoda a su alrededor.

const GAP = 8; // pequeño margen para que no queden pegadas borde con borde

export const rectsOverlap = (a, b) => (
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y
);

// Empuja `card` para que deje de superponerse con `fixed`, moviéndola lo
// menos posible: por el eje (x o y) donde el solapamiento es menor.
const pushApart = (fixed, card) => {
  const overlapX = Math.min(fixed.x + fixed.width, card.x + card.width) - Math.max(fixed.x, card.x);
  const overlapY = Math.min(fixed.y + fixed.height, card.y + card.height) - Math.max(fixed.y, card.y);

  const fixedCenterX = fixed.x + fixed.width / 2;
  const fixedCenterY = fixed.y + fixed.height / 2;
  const cardCenterX = card.x + card.width / 2;
  const cardCenterY = card.y + card.height / 2;

  if (overlapX <= overlapY) {
    const dir = cardCenterX >= fixedCenterX ? 1 : -1;
    return { ...card, x: Math.max(0, card.x + dir * (overlapX + GAP)) };
  }
  const dir = cardCenterY >= fixedCenterY ? 1 : -1;
  return { ...card, y: Math.max(0, card.y + dir * (overlapY + GAP)) };
};

// Cuánto de la card más chica (arrastrada vs. candidata) tiene que quedar
// cubierto para asumir que el usuario quiere soltar "encima" de ella a
// propósito, y no solo pasar cerca / rozarla de paso.
const SWAP_OVERLAP_RATIO = 0.5;

const overlapArea = (a, b) => {
  const ox = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const oy = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return Math.max(0, ox) * Math.max(0, oy);
};

// Si la card arrastrada termina cubriendo la mayor parte de otra card, en
// vez de simplemente empujarla a un costado (lo que puede dejar huecos
// raros o correrla lejos de donde estaba), se intercambian de POSICIÓN: la
// otra card pasa a ocupar el lugar que tenía la arrastrada antes de este
// drag (mantiene su propio ancho/alto, solo cambia dónde está).
const findSwapTarget = (layout, draggedId, draggedRect, originalRect, swapThreshold) => {
  const draggedArea = draggedRect.width * draggedRect.height;
  let best = null;
  let bestRatio = 0;

  for (const c of layout) {
    if (c.id === draggedId) continue;
    if ((originalRect.filteredTab || "") !== (c.filteredTab || "")) continue;
    if (!rectsOverlap(draggedRect, c)) continue;

    const smallerArea = Math.min(draggedArea, c.width * c.height) || 1;
    const ratio = overlapArea(draggedRect, c) / smallerArea;

    if (ratio >= swapThreshold && ratio > bestRatio) {
      bestRatio = ratio;
      best = c;
    }
  }

  return best;
};

// Resuelve el layout completo dado que `draggedId` está (temporal o
// definitivamente) en `draggedRect` {x,y,width,height}. Devuelve la lista
// completa de cards con posiciones ajustadas y el set de ids que cambiaron
// respecto a la posición que traían (sin contar la propia card arrastrada,
// que el llamador ya conoce).
export function resolveLayout(cards, draggedId, draggedRect, options = {}) {
  const { maxPasses = 8, swapThreshold = SWAP_OVERLAP_RATIO } = options;

  const original = cards.find((c) => c.id === draggedId);

  let layout = cards.map((c) =>
    c.id === draggedId ? { ...c, ...draggedRect } : { ...c }
  );

  const changedIds = new Set();

  if (original) {
    const swapTarget = findSwapTarget(layout, draggedId, draggedRect, original, swapThreshold);
    if (swapTarget) {
      layout = layout.map((c) =>
        c.id === swapTarget.id ? { ...c, x: original.x, y: original.y } : c
      );
      changedIds.add(swapTarget.id);
    }
  }

  for (let pass = 0; pass < maxPasses; pass++) {
    let movedSomething = false;

    for (let i = 0; i < layout.length; i++) {
      const a = layout[i];
      for (let j = 0; j < layout.length; j++) {
        if (i === j) continue;
        const b = layout[j];
        if (b.id === draggedId) continue; // la arrastrada nunca se mueve, es el obstáculo
        if ((a.filteredTab || "") !== (b.filteredTab || "")) continue; // otro tab, ni se ven

        if (!rectsOverlap(a, b)) continue;

        const pushed = pushApart(a, b);
        if (pushed.x !== b.x || pushed.y !== b.y) {
          layout[j] = pushed;
          changedIds.add(b.id);
          movedSomething = true;
        }
      }
    }

    if (!movedSomething) break;
  }

  return { layout, changedIds };
}
